import { hasPendingMutations } from "../apollo/pending-mutations";

/**
 * Keeps a long-lived tab on the current build. Phones hold tabs from old SMS
 * links for days, and a restored tab resumes on whatever JavaScript it loaded,
 * so a tab checks `/version.json` (see `appVersionPlugin`) whenever it could
 * have fallen behind: on load, on becoming visible again, on reconnecting, and
 * every few minutes while on screen.
 *
 * Getting onto a new build takes two steps. `prepareUpdate` does whatever
 * the app needs before a reload can land on the new build (the player app
 * waits for its new service worker) and interrupts nothing. Only once it has
 * resolved is the reload itself weighed, and it is then instant, so the
 * moment it is judged safe is the moment it happens.
 *
 * A reload is safe before the user has touched the page since it loaded or
 * came back into view, and while no dialog is open and no form field has
 * focus. Otherwise it waits for the next time the tab returns from the
 * background. A kiosk has no user to disturb and ignores the touch rule.
 * Nothing reloads while a mutation is still in flight.
 */

export type VersionWatchOptions = {
  /** The running bundle's ID, `__APP_BUILD_ID__`. */
  currentBuildId: string;
  /**
   * Resolves once a reload will land on the new build; rejects to be retried
   * at the next check. Default: resolves at once.
   */
  prepareUpdate?: () => Promise<void>;
  /** Moves the tab onto the new build. Default: a plain reload. */
  applyUpdate?: () => void;
  /** Apply as soon as a new build is seen, regardless of interaction. */
  kiosk?: boolean;
  /** Check cadence while the tab is visible. */
  intervalMs?: number;
};

export type UpdateDecision =
  "none" | "prepare" | "defer" | "wait-idle" | "apply";

/** Pure, so the timing rule is unit-testable. */
export const decideUpdate = ({
  updateAvailable,
  prepared,
  kiosk,
  interacted,
  editing,
  busy,
}: {
  updateAvailable: boolean;
  prepared: boolean;
  kiosk: boolean;
  interacted: boolean;
  editing: boolean;
  busy: boolean;
}): UpdateDecision => {
  if (!updateAvailable) return "none";
  if (!prepared) return "prepare";
  if (editing || (!kiosk && interacted)) return "defer";
  if (busy) return "wait-idle";
  return "apply";
};

/**
 * Anything a reload would throw away: an open sheet or dialog, or a focused
 * form field. Covers Radix dialogs and the player BottomSheet (`role=dialog`);
 * a hand-rolled overlay opts in with `data-defers-reload`.
 */
const userIsEditing = (): boolean => {
  if (document.querySelector('[role="dialog"], [data-defers-reload]')) {
    return true;
  }
  const el = document.activeElement;
  if (!el || !(el instanceof HTMLElement)) return false;
  return el.isContentEditable || el.matches("input, textarea, select");
};

const DEFAULT_INTERVAL_MS = 5 * 60_000;
const BUSY_RETRY_MS = 1_000;

/** sessionStorage: the build ID this tab last reloaded to reach. If a reload
 *  lands on the old bundle again, don't try that same build a second time. */
const ATTEMPT_KEY = "polf-version-reload-target";

export const startVersionWatch = ({
  currentBuildId,
  prepareUpdate = () => Promise.resolve(),
  applyUpdate = () => window.location.reload(),
  kiosk = false,
  intervalMs = DEFAULT_INTERVAL_MS,
}: VersionWatchOptions): (() => void) => {
  let latest: string | null = null;
  let interacted = false;
  let applying = false;
  let checking = false;
  let prepared = false;
  let preparing = false;
  let busyTimer: ReturnType<typeof setTimeout> | undefined;

  const alreadyAttempted = (buildId: string) => {
    try {
      return sessionStorage.getItem(ATTEMPT_KEY) === buildId;
    } catch {
      return false;
    }
  };

  const updateAvailable = () =>
    latest !== null && latest !== currentBuildId && !alreadyAttempted(latest);

  const tryApply = () => {
    if (applying || document.hidden) return;
    const decision = decideUpdate({
      updateAvailable: updateAvailable(),
      prepared,
      kiosk,
      interacted,
      editing: userIsEditing(),
      busy: hasPendingMutations(),
    });
    if (decision === "prepare") {
      if (preparing) return;
      preparing = true;
      prepareUpdate().then(
        () => {
          preparing = false;
          prepared = true;
          tryApply();
        },
        () => {
          preparing = false;
        },
      );
      return;
    }
    if (decision === "wait-idle") {
      clearTimeout(busyTimer);
      busyTimer = setTimeout(tryApply, BUSY_RETRY_MS);
      return;
    }
    if (decision !== "apply" || latest === null) return;
    applying = true;
    try {
      sessionStorage.setItem(ATTEMPT_KEY, latest);
    } catch {
      // Storage blocked: the in-memory `applying` flag still stops a repeat
      // within this page load.
    }
    applyUpdate();
  };

  const check = async () => {
    if (checking || applying || document.hidden) return;
    checking = true;
    try {
      const res = await fetch("/version.json", { cache: "no-store" });
      if (res.ok) {
        const body = (await res.json()) as { buildId?: unknown };
        if (typeof body.buildId === "string") latest = body.buildId;
      }
    } catch {
      // Offline or mid-deploy; the next trigger tries again.
    } finally {
      checking = false;
    }
    tryApply();
  };

  const onVisible = () => {
    if (document.hidden) return;
    interacted = false;
    tryApply();
    void check();
  };
  const onPageShow = (e: PageTransitionEvent) => {
    if (e.persisted) onVisible();
  };
  const onInteract = () => {
    interacted = true;
  };
  const onOnline = () => void check();

  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("online", onOnline);
  window.addEventListener("pointerdown", onInteract, {
    capture: true,
    passive: true,
  });
  window.addEventListener("keydown", onInteract, { capture: true });
  const interval = setInterval(() => void check(), intervalMs);
  void check();

  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("pageshow", onPageShow);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("pointerdown", onInteract, { capture: true });
    window.removeEventListener("keydown", onInteract, { capture: true });
    clearInterval(interval);
    clearTimeout(busyTimer);
  };
};
